import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  switchMap,
  tap,
} from 'rxjs/operators';
import { from, Subject } from 'rxjs';

import { Order } from '../../../models/order.model';
import { CostCenter } from '../../../models/cost-center.model';
import { CreditCard } from '../../../models/credit-card.model';
import { PaymentService } from '../../../services/payments.service';
import { PaymentMethod } from '../../../models/payment-method.model';
import { OrderTypes } from '../../../enums/order-types';
import { AuthService } from '../../../services/auth.service';
import { ModalService } from '../../../services/modal.service';
import { DoaService } from '../../../services/doa.service';
import { Doa } from '../../../models/doa.model';
import { isEmptyArray } from '../../../shared/utils';
import { ErrorCodes } from '../../../enums/error.codes';
import { MobileCheckServiceService } from '../../../services/mobile-check-service.service';
import { PaymentMethodVendor } from '../../../enums/payment-method';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';

const ACCOUNT_PAYMENT_TYPE = 'account';
const CREDIT_CARD_PAYMENT_TYPE = 'credit-card';
const CC_KEYS_TO_PICK = [
  'last_four',
  'label',
  'expires_at',
  'name',
  'label',
  'city',
  'zip',
  'country',
  'vendor',
];

@UntilDestroy()
@Component({
  selector: 'app-payment-method-selector',
  templateUrl: './payment-method-selector.component.html',
  styleUrls: ['./payment-method-selector.component.scss'],
  standalone: false,
})
export class PaymentMethodSelectorComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @Input() showCreditCardInfo = false;
  @Input() showDOA = false;
  @Input() verifyingSAP = false;
  @Input() updatingPaymentMethod = false;
  @Input() useDefaultPayment = false;
  @Input() isModal = false;

  @Output() savedCard = new EventEmitter<boolean>();
  @Output() paymentChanged = new EventEmitter<PaymentMethod>();

  selectedCostCenter: CostCenter;
  selectedCostCenterId: number;
  selectedCreditCard: CreditCard;
  selectedCreditCardId: number;
  selectedEmployeeId: string;

  paymentType = ''; // by default
  paymentMethodVendor = PaymentMethodVendor;

  // sap search
  sapSearchResults: CostCenter[] = [];
  sapSearchLoading = false;
  sapSearchSubject: Subject<string> = new Subject<string>();

  // credit card search
  ccSearchResults: CreditCard[] = [];
  ccSearchLoading = false;
  ccSearchSubject: Subject<string> = new Subject<string>();

  // sap search
  employeeSearchResults: Doa[] = [];
  employeeSearchLoading = false;
  employeeSearchSubject: Subject<string> = new Subject<string>();

  saveCard = false;
  isSaveCheckboxVisible = false;

  creditCards: CreditCard[] = [];
  costCenters: CostCenter[] = [];

  radioButtonName = '';
  accountButtonId = '';
  creditCardButtonId = '';

  private allEmployee: Doa[] = [];
  private sapSearchTerm: string;
  private initialized = false;

  _order: Order;

  @Input() set order(val: Order) {
    if (val) {
      this._order = val;

      if (!this.initialized) {
        this.initSAPSearch();
        this.initCCSearch();
        this.addCreditCartToList(this._order.creditCard);
        this.initialized = true;
      }
    }
  }

  constructor(
    private paymentService: PaymentService,
    private authService: AuthService,
    private modalService: ModalService,
    // private toastr: ToastrService,
    private doaService: DoaService,
    private mobileCheck: MobileCheckServiceService
  ) {
    this.radioButtonName = `payment-type-${new Date().valueOf()}`;
    this.accountButtonId = `radio_account-${new Date().valueOf()}`;
    this.creditCardButtonId = `radio_credit_card-${new Date().valueOf()}`;
  }

  ngOnInit(): void {
    this.initEmployeeSearch();
  }

  ngAfterViewInit(): void {
    this.initSelectedFields(this._order);
  }

  ngOnDestroy(): void {
    this._order = null;
  }

  // add new sap
  addSap(): void {
    this.modalService.showSapAccountDialog().then((costCenter) => {
      if (costCenter) {
        // add new cost center to dropdown
        this.costCenters.push(costCenter);
        this.sapSearchResults = this.costCenters.slice();

        this.setPaymentMethod(costCenter);
        this.paymentChanged.emit(costCenter);
      }
    });
  }

  addCreditCard(): void {
    this.paymentService
      .createCreditCard(this._order.id)
      .pipe(
        untilDestroyed(this),
        switchMap((creditCard: CreditCard) => {
          return from(
            this.modalService.showCreditCardDialog(
              this._order.id,
              creditCard,
              this.saveCard
            )
          );
          // if (!creditCard) {
          //   this.toastr.error(
          //     'Cannot add a new credit card to the order. Please contact the support.'
          //   );
          //   return of(null);
          // } else {
          //   return from(
          //     this.modalService.showCreditCardDialog(
          //       this._order.id,
          //       creditCard,
          //       this.saveCard
          //     )
          //   );
          // }
        })
      )
      .subscribe((creditCard) => {
        if (creditCard) {
          this.checkCreditCard(creditCard);
          this.paymentChanged.emit(creditCard);
        }
      });
  }

  onSelectPaymentMethod(paymentMethod: PaymentMethod | any): void {
    this.setPaymentMethod(paymentMethod);
    this.selectDOA();
    this.paymentChanged.emit(paymentMethod);
  }

  onSaveCreditCard(): void {
    this.savedCard.emit(this.saveCard);
  }

  private setPaymentMethod(val: PaymentMethod): void {
    if (!val) {
      return;
    }

    if (val.isAccount()) {
      this.selectedCostCenter = new CostCenter(val as CostCenter);
      this.selectedCostCenterId = this.selectedCostCenter.id;
      this.selectDOA();
    } else if (this.canUseCreditCards) {
      this.selectedCreditCard = new CreditCard(val as CreditCard);
      this.selectedCreditCardId = this.selectedCreditCard.id;
    }
  }

  private checkCreditCard(card: CreditCard): void {
    if (card) {
      this.isSaveCheckboxVisible = true;
      this.addCreditCartToList(card);
      this.setPaymentMethod(card);
    }
  }

  private addCreditCartToList(card: CreditCard): void {
    if (card) {
      const foundIndex = this.creditCards.findIndex((c) => {
        const isEq = isEqual(
          pick(card, CC_KEYS_TO_PICK),
          pick(c, CC_KEYS_TO_PICK)
        );

        if (isEq && card.id === -1 && c.id !== -1) {
          return true;
        }

        return false;
      });

      if (foundIndex === -1) {
        this.creditCards.push(card);
      } else {
        this.creditCards[foundIndex] = card;
      }
    }

    this.ccSearchResults = this.creditCards.slice();
  }

  paymentTypeChanged() {
    let paymentMethod: PaymentMethod;
    if (this.paymentType === ACCOUNT_PAYMENT_TYPE) {
      paymentMethod = this.canEditSAP ? this.selectedCostCenter : null;
    } else {
      paymentMethod = this.selectedCreditCard;
    }
    this.onPaymentChanged(paymentMethod);
  }

  private filterEmployee(employeeList: Doa[], term: string): Doa[] {
    if (isEmptyArray(employeeList) || !term) {
      return employeeList;
    }

    const searchText = term.trim().toLowerCase();
    return this.allEmployee.filter(
      (emp) => emp.fullName.toLowerCase().indexOf(searchText) !== -1
    );
  }

  get hasEmployees(): boolean {
    return !isEmptyArray(this.allEmployee);
  }

  get performingUpdate(): boolean {
    return this.verifyingSAP || this.updatingPaymentMethod;
  }

  onSelectEmployee(val: Doa) {
    if (!val || !this.selectedCostCenter) {
      return;
    }
    this.selectedEmployeeId = val.employee_id;

    const newCostCenter = new CostCenter(this.selectedCostCenter);
    newCostCenter.employee_id = val.employee_id;
    newCostCenter.first_name = val.first_name;
    newCostCenter.last_name = val.last_name;
    newCostCenter.limit_per_po = +val.limit_per_po;

    this.paymentChanged.emit(newCostCenter);
  }

  get isLimitExceeded(): boolean {
    if (!this._order) {
      return false;
    }

    return this._order.error?.errorCode === ErrorCodes.DOA_EXCEED_LIMIT;
  }

  get isSapInvalid(): boolean {
    if (!this._order) {
      return false;
    }

    return this._order.error?.errorCode === ErrorCodes.INVALID_SAP;
  }

  private onPaymentChanged(paymentMethod: PaymentMethod) {
    this.paymentChanged.emit(paymentMethod);
  }

  private setPaymentType() {
    if (this.paymentType) {
      return;
    }

    if (this.selectedCreditCard) {
      this.paymentType = CREDIT_CARD_PAYMENT_TYPE;
    } else {
      this.paymentType = ACCOUNT_PAYMENT_TYPE;
    }
  }

  // init CostCenter dropdown
  private initSAPSearch() {
    if (!this.canEditSAP) {
      return;
    }
    this.costCenters = [...this.paymentService.sapAccounts];
    this.sapSearchResults = this.costCenters;

    this.sapSearchSubject
      .pipe(
        untilDestroyed(this),
        debounceTime(400),
        distinctUntilChanged(),
        tap(() => {
          this.sapSearchLoading = true;
        })
      )
      .subscribe((term) => {
        this.sapSearchTerm = term;
        this.sapSearchResults =
          this.paymentService.findPaymentMethodsByTerm<CostCenter>(
            term,
            this.paymentService.sapAccounts
          );
        this.sapSearchLoading = false;
      });
  }

  // init CreditCard dropdown
  private initCCSearch() {
    if (!this.canUseCreditCards) {
      return;
    }

    this.creditCards = [...this.paymentService.creditCards];
    this.ccSearchResults = this.creditCards;

    this.ccSearchSubject
      .pipe(
        untilDestroyed(this),
        debounceTime(400),
        distinctUntilChanged(),
        tap(() => {
          this.ccSearchLoading = true;
        })
      )
      .subscribe((term) => {
        if (term?.length > 0) {
          this.ccSearchResults =
            this.paymentService.findPaymentMethodsByTerm<CreditCard>(
              term,
              this.paymentService.creditCards
            );
        }
        this.ccSearchLoading = false;
      });
  }

  // init Employee dropdown
  private initEmployeeSearch() {
    if (!this.showDOA) {
      return;
    }
    this.doaService
      .fetchAllEmployee()
      .pipe(untilDestroyed(this))
      .subscribe((result) => {
        this.allEmployee = result;
        this.employeeSearchResults = this.filterEmployee(this.allEmployee, '');
        this.selectDOA();
      });

    this.employeeSearchSubject
      .pipe(
        untilDestroyed(this),
        debounceTime(400),
        filter((search) => search && search.length > 0),
        tap(() => {
          this.employeeSearchLoading = true;
        })
      )
      .subscribe((term) => {
        this.employeeSearchResults = this.filterEmployee(
          this.allEmployee,
          term
        );
        this.employeeSearchLoading = false;
      });
  }

  private selectDOA() {
    if (
      this.selectedCostCenter &&
      this.selectedCostCenter.employee_id &&
      !this._order.error
    ) {
      // check if user exists
      this.selectedEmployeeId = this.allEmployee.find(
        (e) => e.employee_id === this.selectedCostCenter.employee_id
      )?.employee_id;
    }
  }

  private initSelectedFields(order: Order) {
    if (!order) {
      return;
    }

    if (order.costCenter) {
      this.selectedCostCenter = order.costCenter;
    }

    if (order.creditCard && order.isOnDemand) {
      this.selectedCreditCard = order.creditCard;
    }

    if (!this.selectedCreditCard && !this.selectedCostCenter) {
      //  for employee  one payment method must be selected
      if (this.useDefaultPayment) {
        const paymentMethod =
          this.paymentService.getDefaultPaymentForOrder(order);
        if (paymentMethod) {
          if (paymentMethod.isAccount()) {
            this.selectedCostCenter = paymentMethod as CostCenter;
          } else {
            this.selectedCreditCard = paymentMethod as CreditCard;
          }
          setTimeout(() => {
            this.paymentChanged.emit(paymentMethod);
          });
        }
      }
    }

    setTimeout(() => {
      this.selectedCostCenterId = this.selectedCostCenter?.id;
      this.selectedCreditCardId = this.selectedCreditCard?.id;
      this.selectDOA();
      this.setPaymentType();
    });
  }

  get canAddCreditCard(): boolean {
    return this.authService.canEditCreditCard;
  }

  get canUseCreditCards(): boolean {
    return (
      this._order &&
      this._order.type === OrderTypes.ECOMMERCE_TYPE &&
      this.authService.canEditCreditCard
    );
  }

  get canEditSAP(): boolean {
    return this.authService.canEditSAP;
  }

  get mobileDevice(): boolean {
    return this.mobileCheck.mobileDevice;
  }
}
